// Prevent JSON.stringify from throwing on DOM elements that carry React's
// internal __reactFiber$ property, which creates a circular reference via
// stateNode back to the DOM node. Any third-party SDK that intercepts DOM
// mutations and tries to serialize the element will hit this guard instead.
// Patching Element.prototype covers HTMLElement, SVGElement, and all other
// DOM node types in one place.
if (typeof Element !== 'undefined' && !('toJSON' in Element.prototype)) {
  // @ts-expect-error deliberate, toJSON isn't in lib.dom
  Element.prototype.toJSON = function () {
    return null;
  };
}

// Polyfill String.prototype.replaceAll for older browsers (Edge <85, etc.)
// query-string v9 and other deps use replaceAll which is unavailable in Edge 83.
if (!String.prototype.replaceAll) {
  String.prototype.replaceAll = function (
    search: string | RegExp,
    replace: string | ((substring: string, ...args: unknown[]) => string)
  ): string {
    if (search instanceof RegExp) {
      return this.replace(search, replace as never);
    }
    return this.split(search as string).join(replace as string);
  };
}

import { fetchAuthSession } from '@aws-amplify/core';
import { setOnUnauthorized } from '@chiroup/client-core/functions/ChiroUpAPI';
import { Safari16API } from '@chiroup/client-core/functions/Safari16API';
import { initSentryFrontend } from '@chiroup/core/config/sentryFrontend';
import * as Sentry from '@sentry/react';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { MinimizedDockProvider } from './contexts/minimizedDock.context';
import './styles/index.css';
import { enhancedSignOut } from './utils/authUtils';

/// Initialize React for global access
window.React = React;

const VITE_CHIROUP_API = import.meta.env.VITE_CHIROUP_API as string;

// Initialize Sentry with shared configuration
initSentryFrontend({
  dsn: 'https://4e31ca6b060ff97a5d318de5b6ad29a8@o4506354335612928.ingest.sentry.io/4506354340265984',
  apiEndpoint: VITE_CHIROUP_API,
  additionalApiEndpoints: [import.meta.env.VITE_CONNECTION_API as string],
  stage: import.meta.env.VITE_STAGE,
  version: import.meta.env.VITE_VERSION,
});

const authConfig: ResourcesConfig['Auth'] = {
  Cognito: {
    allowGuestAccess: false,
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
    userPoolClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID as string,
    identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID as string,
  },
};

Amplify.configure(
  {
    Auth: authConfig,
    API: {
      REST: {
        api: {
          endpoint: import.meta.env.VITE_CHIROUP_API as string,
        },
        'api-noauth': {
          endpoint: import.meta.env.VITE_CHIROUP_API as string,
        },
        connectionApi: {
          endpoint: import.meta.env.VITE_CONNECTION_API as string,
        },
      },
    },
  },
  {
    API: {
      REST: {
        headers: async () => {
          let authToken: string | undefined;
          try {
            authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
          } catch {
            // Amplify's cross-tab auth messaging can time out on iOS Safari
            // ("Message Timeout") when a Cognito refresh token is stale.
            // Returning empty headers lets the API call fail with a 401,
            // which the setOnUnauthorized handler will catch and sign out.
          }
          return {
            Authorization: `Bearer ${authToken ?? ''}`,
          };
        },
        retryStrategy: {
          strategy: 'no-retry',
        },
      },
    },
  }
);

// Sign the user out when any API call comes back with `sessionInvalid: true`
// so they see a login screen instead of an infinite spinner. Sign-out goes
// first so it never depends on the breadcrumb succeeding (Sentry not loaded,
// blocked by an ad-blocker, etc.). The breadcrumb gives us telemetry to
// catch any false positives in production — messages are static auth
// strings, not PHI.
setOnUnauthorized(({ statusCode, message }) => {
  enhancedSignOut().catch(() => {});
  Sentry.addBreadcrumb({
    category: 'auth',
    level: 'warning',
    message: `Auto sign-out (${statusCode ?? '?'}): ${(message ?? '').slice(0, 80)}`,
  });
});

// Configure Safari16API fallback for Safari 16.0 compatibility
Safari16API.configure({
  api: import.meta.env.VITE_CHIROUP_API as string,
  'api-noauth': import.meta.env.VITE_CHIROUP_API as string,
});

// Ensure the root element exists
const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element not found');
}

// Create root and render
const root = createRoot(container);

// Device fingerprinting (Fingerprint.com) for trial-signup abuse prevention is
// scoped to the flows that use it (signup, clinic creation) via FingerprintScope
// rather than wrapped around the whole app — that previously loaded the agent
// script on every route, so a CDN block by tracking protection surfaced as an
// unhandled rejection on unrelated pages.
const appTree = (
  // <React.StrictMode>
  // <AppMRUProvider>
  <MinimizedDockProvider>
    <App />
  </MinimizedDockProvider>
  // </AppMRUProvider>
  // </React.StrictMode>
);

root.render(appTree);
// [2025-07-03 by Brian] Disabled StrictMode per JG as it was doubling
// expensive REST calls when running locally.
